import * as yup from 'yup';

import { ItemDisplayUnit, MandatoryWarning, UserRequirement } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { VatInputType } from 'modules/menu/components/VatCategories';
import {
  yupTestMonetaryDecimals,
  yupTestUpToDecimalPlaces,
} from 'modules/menu/items/components/ItemDetailsForm/validationSchema';

import { FormValues, InventoryManagementFormValues } from './types';
import { OverridableRowGroup } from '../../constants';
import { OptionBinding, SharedMenuDietaryPreference } from '../../types';

export type ValidationContext = {
  currency: Currency;
  currOverriddenRowGroups: OverridableRowGroup[];
};

const isValidBarcodeDigit = (value: string) => {
  const val = value.slice(0, -1).padStart(13, '0');
  const checkDigit = String(
    (10 -
      (val
        .split('')
        .map((num, idx) => +num * (idx % 2 === 0 ? 3 : 1))
        .reduce((prev, cur) => prev + cur) %
        10)) %
      10,
  );
  return checkDigit === value.slice(-1);
};

const isValidBarcodeLength = (value: string) => [8, 12, 13, 14].includes(value.length);

const yupTestBarCodeLength = {
  name: 'barcodeLength',
  message: { id: 'validation.enterValidBarcode' },
  test: (value: string | undefined) => !value || isValidBarcodeLength(value),
};

const yupTestBarcodeDigits = {
  name: 'barcodeCheckDigit',
  message: { id: 'validation.enterValidBarcodeDigit' },
  test: (value: string | undefined) => !value || isValidBarcodeDigit(value),
};

const itemDetailsSchema = yup
  .object({
    alcoholPercentage: yup.number().min(0).max(100).test(yupTestUpToDecimalPlaces(1)).required(),
    availabilityPeriod: yup
      .mixed<FormValues['availabilityPeriod']>()
      .test('endIsAfterStart', { id: 'validation.mustBeEndAfterStart' }, value => {
        if (value && value.start && value.end && value.end <= value.start) {
          return false;
        }
        return true;
      })
      .required(),
    availabilityPeriodOverride: yup
      .mixed<FormValues['availabilityPeriodOverride']>()
      .test('endIsAfterStart', { id: 'validation.mustBeEndAfterStart' }, value => {
        if (value && value.start && value.end && value.end <= value.start) {
          return false;
        }
        return true;
      })
      .when('$currOverriddenRowGroups', (currOverriddenRowGroups: OverridableRowGroup[], schema: yup.BaseSchema) =>
        currOverriddenRowGroups.includes('AVAILABILITY') ? schema.required() : schema,
      ),
    barcodeGtin: yup.string().test(yupTestBarCodeLength).test(yupTestBarcodeDigits),
    brandId: yup.string().nullable().defined(),
    caffeineContentValue: yup
      .number()
      .nullable()
      .positive()
      .max(100_000)
      .when('caffeineContentUnits', (caffeineContentUnits, schema: yup.BaseSchema) =>
        !!caffeineContentUnits ? schema.required() : schema,
      ),
    caffeineContentUnits: yup.mixed<FormValues['caffeineContentUnits']>(),
    categoryId: yup.string().nullable().defined(),
    description: yup.string().trim(),
    disableBikeDelivery: yup.boolean().required(),
    disableCarDelivery: yup.boolean().required(),
    disableMotorcycleDelivery: yup.boolean().required(),
    fixedDiscountPrice: yup
      .number()
      .min(0)
      .test(yupTestMonetaryDecimals)
      .when('discountType', (discountType: 'absolute' | 'relative' | undefined, schema: yup.NumberSchema) =>
        discountType === 'absolute' ? schema.required() : schema,
      )
      .when('price', (price: number | undefined, schema: yup.NumberSchema) =>
        price !== undefined ? schema.lessThan(price) : schema,
      ),
    fixedDiscountPriceOverride: yup
      .number()
      .min(0)
      .test(yupTestMonetaryDecimals)
      .when(
        'discountTypeOverride',
        (discountTypeOverride: 'absolute' | 'relative' | undefined, schema: yup.NumberSchema) =>
          discountTypeOverride === 'absolute' ? schema.required() : schema,
      )
      .when('priceOverride', (priceOverride: number | undefined, schema: yup.NumberSchema) =>
        priceOverride !== undefined ? schema.lessThan(priceOverride) : schema,
      ),
    discountPercentage: yup
      .number()
      .min(0)
      .max(100)
      .test(yupTestUpToDecimalPlaces(0))
      .when('discountType', (discountType: 'absolute' | 'relative' | undefined, schema: yup.NumberSchema) =>
        discountType === 'relative' ? schema.required() : schema,
      ),
    discountPercentageOverride: yup
      .number()
      .min(0)
      .max(100)
      .test(yupTestUpToDecimalPlaces(0))
      .when(
        'discountTypeOverride',
        (discountTypeOverride: 'absolute' | 'relative' | undefined, schema: yup.NumberSchema) =>
          discountTypeOverride === 'relative' ? schema.required() : schema,
      ),
    discountType: yup.mixed<FormValues['discountType']>(),
    discountTypeOverride: yup.mixed<FormValues['discountTypeOverride']>(),
    volumeInMl: yup.number().positive(),
    weightInGrams: yup.number().positive(),
    numberOfUnits: yup.number().positive(),
    displayUnit: yup.mixed<ItemDisplayUnit>().nullable().defined(),
    dietaryPreferences: yup.mixed<SharedMenuDietaryPreference[]>().defined(),
    ingredients: yup.string(),
    allergens: yup.string(),
    additives: yup.string(),
    nutritionFacts: yup.string(),
    nutritionValues: yup.array().of(
      yup.object({
        id: yup.string().required(),
        value: yup.string().trim(),
      }),
    ),
    nutritionValuesBaseline: yup.mixed<'per_100g' | 'per_100ml'>().when('nutritionValues', {
      is: (nutritionValues: { id: string; value?: string }[]) => nutritionValues?.some(n => n.value !== undefined),
      then: schema => schema.required(),
    }),
    countryOfOrigin: yup.string(),
    usageInstructions: yup.string(),
    producerInfo: yup.string(),
    distributorInfo: yup.string(),
    bundleOffer: yup.boolean().required(),
    depositOption: yup.string(),
    mandatoryWarnings: yup.mixed<MandatoryWarning[]>().defined(),
    maxQuantityPerPurchase: yup
      .number()
      .positive()
      .integer()
      .when(['weightType', 'sellByWeightInputType', 'gramsPerStep', 'minQuantityPerPurchase'], ((
        weightType: 'pre-packaged' | 'weighted-items' | undefined,
        sellByWeightInputType: 'grams' | 'number_of_items' | undefined,
        gramsPerStep: number | undefined,
        minQuantityPerPurchase: number | undefined,
      ) => {
        if (minQuantityPerPurchase !== undefined) {
          return yup.number().positive().integer().min(minQuantityPerPurchase);
        }
        return weightType === 'weighted-items' && sellByWeightInputType === 'grams' && gramsPerStep !== undefined
          ? yup.number().positive().integer().min(gramsPerStep, {
              id: 'validation.mustBeGreaterOrEqualThanMinimumWeight',
            })
          : yup.number().positive().integer();
        // it seems that the method overload is mistyped so I've cast to any to avoid TS error
      }) as any),
    minQuantityPerPurchase: yup
      .number()
      .positive()
      .integer()
      .when(['weightType', 'sellByWeightInputType', 'gramsPerStep'], ((
        weightType: 'pre-packaged' | 'weighted-items' | undefined,
        sellByWeightInputType: 'grams' | 'number_of_items' | undefined,
        gramsPerStep: number | undefined,
      ) => {
        return weightType === 'weighted-items' && sellByWeightInputType === 'grams' && gramsPerStep !== undefined
          ? yup.number().positive().integer().min(gramsPerStep, {
              id: 'validation.mustBeGreaterOrEqualThanMinimumWeight',
            })
          : yup.number().positive().integer();
        // it seems that the method overload is mistyped so I've cast to any to avoid TS error
      }) as any),
    enabled: yup.boolean().required(),
    enabledOverride: yup
      .boolean()
      .when('$currOverriddenRowGroups', (currOverriddenRowGroups: OverridableRowGroup[], schema: yup.BaseSchema) =>
        currOverriddenRowGroups.includes('AVAILABILITY') ? schema.required() : schema,
      ),
    enableDelivery: yup.boolean().required(),
    enableEatin: yup.boolean().required(),
    enableTakeaway: yup.boolean().required(),
    image: yup.mixed<FormValues['image']>().required(),
    merchantSku: yup.string().trim(),
    merchantSkuOverride: yup.string().trim(),
    moreInformation: yup.string(),
    name: yup.string().trim().required(),
    posId: yup.string(),
    posIdOverride: yup.string(),
    price: yup.number().min(0).test(yupTestMonetaryDecimals).required(),
    priceOverride: yup
      .number()
      .min(0)
      .when('$currOverriddenRowGroups', (currOverriddenRowGroups: OverridableRowGroup[], schema: yup.BaseSchema) =>
        currOverriddenRowGroups.includes('PRICE_AND_DISCOUNT')
          ? schema.test(yupTestMonetaryDecimals).required()
          : schema,
      ),
    productHierarchyLevelId: yup.string(),
    productId: yup.string().notRequired(),
    source: yup.string(),
    useWeeklyAvailability: yup.boolean().required(),
    useWeeklyAvailabilityOverride: yup
      .boolean()
      .when('$currOverriddenRowGroups', (currOverriddenRowGroups: OverridableRowGroup[], schema: yup.BaseSchema) =>
        currOverriddenRowGroups.includes('AVAILABILITY') ? schema.required() : schema,
      ),
    useWeeklyVisibility: yup.boolean().required(),
    useWeeklyVisibilityOverride: yup
      .boolean()
      .when('$currOverriddenRowGroups', (currOverriddenRowGroups: OverridableRowGroup[], schema: yup.BaseSchema) =>
        currOverriddenRowGroups.includes('VISIBILITY') ? schema.required() : schema,
      ),
    userRequirement: yup.mixed<UserRequirement>(),
    vat: yup
      .object({
        type: yup.mixed<VatInputType>(),
        categoryCode: yup.string(),
        percentage: yup.number(),
      })
      .required()
      .test(
        'categoryCode',
        'Please select VAT category',
        vat => vat.categoryCode !== undefined || vat.percentage !== undefined,
      )
      .test(
        'percentage',
        'VAT must be between 0% and 99%',
        vat =>
          vat.type !== 'manual' ||
          (vat.percentage !== undefined &&
            vat.percentage >= 0 &&
            vat.percentage <= 99 &&
            yupTestUpToDecimalPlaces(2).test(vat.percentage)),
      ),
    weeklyAvailability: yup.mixed<FormValues['weeklyAvailability']>().required(),
    weeklyAvailabilityOverride: yup
      .mixed<FormValues['weeklyAvailabilityOverride']>()
      .when('$currOverriddenRowGroups', (currOverriddenRowGroups: OverridableRowGroup[], schema: yup.BaseSchema) =>
        currOverriddenRowGroups.includes('AVAILABILITY') ? schema.required() : schema,
      ),
    weeklyVisibility: yup.mixed<FormValues['weeklyVisibility']>().required(),
    weeklyVisibilityOverride: yup
      .mixed<FormValues['weeklyVisibilityOverride']>()
      .when('$currOverriddenRowGroups', (currOverriddenRowGroups: OverridableRowGroup[], schema: yup.BaseSchema) =>
        currOverriddenRowGroups.includes('VISIBILITY') ? schema.required() : schema,
      ),
    location: yup.string().trim(),
    pickingAreaId: yup.string(),
    pickingAreaSequence: yup.number().integer().positive(),
    weightType: yup.mixed<'pre-packaged' | 'weighted-items'>(),
    sellByWeightInputType: yup.mixed<'grams' | 'number_of_items'>().when('weightType', {
      is: (weightType: 'pre-packaged' | 'weighted-items') => weightType === 'weighted-items',
      then: schema => schema.required(),
    }),
    gramsPerStep: yup
      .number()
      .min(1)
      .when('sellByWeightInputType', {
        is: (sellByWeightInputType: 'grams' | 'number_of_items') =>
          sellByWeightInputType === 'grams' || sellByWeightInputType === 'number_of_items',
        then: schema => schema.required(),
      }),
    optionBindings: yup.array().of(yup.mixed<OptionBinding>().required()),
    externalInfoHyperlink: yup
      .object({
        hyperlink: yup.string().notRequired(),
        text: yup.string().notRequired(),
      })
      .nullable()
      .shape(
        {
          hyperlink: yup.string().when('text', {
            is: undefined,
            then: yup.string().notRequired(),
            otherwise: yup.string().url().required(),
          }),
          text: yup.string().when('hyperlink', {
            is: undefined,
            then: yup.string().notRequired(),
            otherwise: yup.string().required(),
          }),
        },
        [['hyperlink', 'text']],
      ),
    isRestrictedItem: yup.boolean().required(),
    isWoltPlusOnly: yup.boolean(),
    isOTCRestricted: yup.boolean(),
    isAgeRestricted: yup.boolean(),
    ageLimit: yup.number().positive().optional(),
    isDeliveryHandshakeRestricted: yup.boolean(),
    homedeliveryCommission: yup.number().min(0).max(100).test(yupTestUpToDecimalPlaces(2)),
    takeawayCommission: yup.number().min(0).max(100).test(yupTestUpToDecimalPlaces(2)),
    eatinCommission: yup.number().min(0).max(100).test(yupTestUpToDecimalPlaces(2)),
  })
  .required();

const inventoryManagementSchema: yup.SchemaOf<InventoryManagementFormValues> = yup
  .object({
    inventoryMode: yup.mixed().oneOf(['limited_quantity', 'inventory', 'binary']).required(),
    inventoryModeOverride: yup.mixed().oneOf(['limited_quantity', 'inventory', 'binary']),
    inStock: yup.boolean().required(),
    inStockOverride: yup.boolean(),
    inventoryBalance: yup
      .number()
      .integer()
      .min(0)
      .when('inventoryMode', {
        is: (inventoryMode: 'limited_quantity' | 'inventory' | 'binary') => inventoryMode !== 'binary',
        then: schema => schema.required(),
      }),
  })
  .required();

export const validationSchema = itemDetailsSchema.concat(inventoryManagementSchema);

export type ValidFormValues = yup.Asserts<typeof validationSchema>;
